// Single registry mapping (a) sidebar nav items + (b) opened tabs to
// the same MUI icon by string key. Keeping this here (not in
// SidebarLayout) lets TabsBar hydrate icons from sessionStorage records
// (which only carry string keys, not React nodes).

import type { ReactElement } from "react";
import {
  Home as HomeIcon,
  PeopleAlt as AdminUsersIcon,
  Group as AppUsersIcon,
  Work as JobsIcon,
  Notifications as PushIcon,
  Assessment as ReportIcon,
  Settings as ConfigIcon,
  AccountCircle as ProfileIcon,
  CardGiftcard as RewardsIcon,
  SupportAgent as TicketsIcon,
} from "@mui/icons-material";

export type RouteIconKey =
  | "dashboard"
  | "admin-users"
  | "app-users"
  | "jobs"
  | "push"
  | "report"
  | "config"
  | "profile"
  | "rewards"
  | "tickets";

// Map key → factory (rendered fresh per usage so the icon size sx prop
// inside <Tab> / <Sidebar> can override per call site).
const REGISTRY: Record<RouteIconKey, () => ReactElement> = {
  dashboard: () => <HomeIcon />,
  "admin-users": () => <AdminUsersIcon />,
  "app-users": () => <AppUsersIcon />,
  jobs: () => <JobsIcon />,
  push: () => <PushIcon />,
  report: () => <ReportIcon />,
  config: () => <ConfigIcon />,
  profile: () => <ProfileIcon />,
  rewards: () => <RewardsIcon />,
  tickets: () => <TicketsIcon />,
};

export function getRouteIcon(key: string): ReactElement | null {
  if (key in REGISTRY) {
    return REGISTRY[key as RouteIconKey]();
  }
  return null;
}

/** Heuristic: pick the icon key from a path. Used when we open a tab from
 *  a navigation event without an explicit icon parameter. */
export function iconKeyForPath(path: string): RouteIconKey {
  if (path.startsWith("/app/dashboard")) return "dashboard";
  if (path.startsWith("/app/admin-users")) return "admin-users";
  if (path.startsWith("/app/app-users")) return "app-users";
  if (path.startsWith("/app/jobs")) return "jobs";
  if (path.startsWith("/app/notifications")) return "push";
  if (path.startsWith("/app/reports")) return "report";
  if (path.startsWith("/app/config")) return "config";
  if (path.startsWith("/app/rewards")) return "rewards";
  if (path.startsWith("/app/tickets")) return "tickets";
  if (path.startsWith("/app/profile")) return "profile";
  return "dashboard"; // safe fallback
}

/** Canonical tab label for each route — MUST match the sidebar submenu
 *  label that links to the same path. Tab labels are sourced from this
 *  map (not from the sidebar's parent-group label) so clicking
 *  "Add/Edit/Delete Users" opens a tab labelled "Add/Edit/Delete Users",
 *  not the parent group's "Admin users management". */
export function defaultLabelForPath(path: string): string {
  const map: Record<string, string> = {
    "/app/dashboard": "Dashboard",
    "/app/admin-users": "Add/Edit/Delete Users",
    "/app/admin-users/new": "Add admin user",
    "/app/admin-users/me": "My Profile",
    "/app/profile": "My Profile",
    "/app/admin-users/roles": "Roles & Permissions",
    "/app/app-users": "Add/Edit/Delete APP Users",
    "/app/app-users/new": "Add app user",
    "/app/app-users/approval": "Approve account applications",
    "/app/jobs": "Add/Edit Job",
    "/app/notifications/push": "Send push notifications",
    "/app/notifications/email": "Email notifications",
    "/app/reports/commission": "Commission report",
    "/app/reports/billing": "Billing report",
    "/app/config/job-categories": "Job Categories",
    "/app/config/faq": "FAQ Templates",
    "/app/rewards": "Reward Points",
    "/app/tickets": "Support tickets",
  };
  // Exact match first, else strip /:id-style suffixes back to nearest known.
  const exact = map[path];
  if (exact) return exact;
  // E.g. /app/admin-users/abc123 → falls back to "Admin users" with the id
  // visible in the URL bar but a clean tab label.
  const segments = path.split("/").filter(Boolean);
  while (segments.length > 1) {
    segments.pop();
    const trial = "/" + segments.join("/");
    const trialMatch = map[trial];
    if (trialMatch) return trialMatch;
  }
  return "Page";
}
